import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl, formatRON } from "@/lib/apiConfig";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Star, Music2, Mic2, Headphones, Sparkles, Zap, ShieldCheck, ArrowRight, Play } from "lucide-react";
import SakuraPetals from "@/components/SakuraPetals";
import { useTheme } from "@/themes/shared/ThemeContext";
import SEO, { organizationJsonLd, websiteJsonLd, SITE } from "@/components/SEO";
import { FAQ_ITEMS, buildFaqJsonLd } from "@/content/faq";

const GENRES = [
  { name: "Pop", kanji: "歌", desc: "Hituri emoționale" },
  { name: "Trap / Rap", kanji: "韻", desc: "Versuri puternice" },
  { name: "R&B / Soul", kanji: "魂", desc: "Voci catifelate" },
  { name: "Manele", kanji: "宴", desc: "Vibe românesc" },
  { name: "Folk / Acustic", kanji: "森", desc: "Intim și cald" },
  { name: "Rock", kanji: "雷", desc: "Energie pură" },
];

const STEPS = [
  { n: "01", title: "Spune-ne povestea", desc: "Completezi formularul cu detalii despre persoană, ocazie, stil, vibe și mesajul pe care vrei să-l transmită melodia." },
  { n: "02", title: "Transformăm ideea în versuri", desc: "Construim textul piesei pe baza poveștii tale, cu structură, emoție și direcție muzicală potrivită momentului." },
  { n: "03", title: "Dăm viață melodiei", desc: "Pregătim interpretarea, vocea și atmosfera piesei astfel încât rezultatul să sune personal, curat și emoțional." },
  { n: "04", title: "Primești melodia finală", desc: "Îți livrăm piesa digital, în format audio de calitate, gata de trimis, postat sau oferit cadou." },
];

const Home = () => {
  const { siteAssets: contextSiteAssets } = useTheme();
  const [siteAssets, setSiteAssets] = useState(contextSiteAssets || {});
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqItems, setFaqItems] = useState(FAQ_ITEMS);
  const heroUrl = siteAssets?.hero_background_url;

  useEffect(() => {
    fetch(apiUrl("/products")).then((r) => r.json()).then(setProducts).catch(() => {});
    fetch(apiUrl("/reviews")).then((r) => r.json()).then(setReviews).catch(() => {});
    fetch(apiUrl("/faq-items"))
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setFaqItems(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (contextSiteAssets?.hero_background_url) setSiteAssets(contextSiteAssets);
  }, [contextSiteAssets]);

  useEffect(() => {
    fetch(apiUrl("/public/settings/site_assets"))
      .then((r) => r.json())
      .then((data) => setSiteAssets(data?.value || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="text-bone">
      <SEO
        path="/"
        title="TuneForUs · Melodii personalizate pentru cadouri speciale"
        description={SITE.description}
        jsonLd={[
          organizationJsonLd,
          websiteJsonLd,
          buildFaqJsonLd(faqItems),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Melodii personalizate TuneForUs",
            provider: { "@type": "Organization", name: "TuneForUs", url: SITE.url },
            areaServed: "RO",
            serviceType: "Cadouri muzicale personalizate",
            description: SITE.description,
            offers: {
              "@type": "Offer",
              url: `${SITE.url}/comanda`,
              availability: "https://schema.org/InStock",
              priceCurrency: "RON",
            },
          },
        ]}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden" data-testid="hero-section">
        {heroUrl ? (
          <img
            src={heroUrl}
            alt=""
            width="1920"
            height="1080"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-right md:object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_28%,rgba(220,20,60,0.35),transparent_34%),linear-gradient(135deg,#090909_0%,#120408_48%,#000_100%)]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <div className="absolute inset-0 grain" />
        <SakuraPetals />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-crimson border border-crimson/40 px-3 py-1.5">
                CODE — 02 · TuneForUs Unit
              </span>
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                未来はここにある
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.9] text-balance">
              POVESTEA TA<br />
              <span className="text-crimson">DEVINE</span> MELODIE.
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
              Compunem piese 100% personalizate — versuri originale, instrumental, voci profesioniste.
              Cadou unic pentru iubită, aniversare, nuntă sau orice moment care merită un cântec.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/comanda">
                <Button size="lg" data-testid="cta-order-hero" className="bg-crimson hover:bg-crimson-light text-white rounded-none font-mono uppercase tracking-widest text-xs px-8 py-6 crimson-glow">
                  Comandă piesa ta <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/pachete">
                <Button size="lg" variant="outline" data-testid="cta-packages-hero" className="rounded-none border-white/30 hover:bg-white hover:text-black bg-transparent font-mono uppercase tracking-widest text-xs px-8 py-6">
                  Vezi pachete
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-8 text-xs font-mono uppercase tracking-widest text-white/50">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-crimson" /> Garanție revizuiri</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-crimson" /> Express 24h</span>
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-crimson" /> 4.9 / 5 rating</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
          25 · 12 · 24 / SAMURAI UNIT
        </div>
        <div className="absolute bottom-6 right-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
          武士道 / CYBERROCK
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-24 px-6 lg:px-10" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// Process</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">Cum funcționează</h2>
            </div>
            <p className="hidden md:block max-w-sm text-white/60 text-sm">Patru pași simpli. Zero stres. O melodie care nu există în alt loc pe planeta asta.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-ink-800 p-8 hover:bg-ink-700 transition-colors group hud-frame">
                <div className="font-mono text-xs text-crimson tracking-widest">{s.n}</div>
                <h3 className="mt-4 font-display text-2xl font-bold">{s.title}</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{s.desc}</p>
                <div className="mt-8 h-px bg-gradient-to-r from-crimson via-crimson/30 to-transparent group-hover:from-crimson-light" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GENRES */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// Genuri</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              Alege <span className="text-crimson">vibe-ul</span> tău
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {GENRES.map((g) => (
              <div key={g.name} className="group relative p-6 bg-ink-800 border border-white/5 hover:border-crimson hover:bg-black transition-all cursor-pointer corner-cut">
                <div className="font-mono text-3xl text-crimson opacity-50 group-hover:opacity-100 transition-opacity">{g.kanji}</div>
                <div className="mt-4 font-display font-bold text-lg">{g.name}</div>
                <div className="mt-1 text-xs text-white/50">{g.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5" data-testid="packages-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// Pachete</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">Alege pachetul</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <Card key={p.id} data-testid={`product-card-${p.slug}`} className={`relative bg-ink-800 border ${i === 1 ? "border-crimson crimson-glow" : "border-white/10"} rounded-none p-8 flex flex-col hud-frame`}>
                {i === 1 && (
                  <span className="absolute -top-3 left-6 font-mono text-[10px] uppercase tracking-[0.3em] bg-crimson text-white px-3 py-1">Most Wanted</span>
                )}
                <div className="font-mono text-xs uppercase tracking-widest text-white/50">Pachet</div>
                <div className="mt-2 font-display text-3xl font-black tracking-tight">{p.name}</div>
                <div className="mt-4 text-white/60 text-sm leading-relaxed">{p.description}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-black text-crimson">{formatRON(p.price)}</span>
                </div>
                <div className="mt-2 font-mono text-xs text-white/50 uppercase tracking-widest">Livrare: {p.delivery_days}</div>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {p.features?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-white/80"><Sparkles className="w-3 h-3 mt-1 text-crimson flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link to={`/comanda?pachet=${p.slug}`} className="mt-8">
                  <Button data-testid={`btn-order-${p.slug}`} className="w-full rounded-none bg-crimson hover:bg-crimson-light text-white font-mono uppercase tracking-widest text-xs">
                    Alege {p.name}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLES / VISUAL */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// Audio Lab</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              Studio profesionist.<br /><span className="text-crimson">Emoție</span> autentică.
            </h2>
            <p className="mt-6 text-white/70 text-base max-w-lg leading-relaxed">
              Fiecare piesă trece printr-un proces atent de construcție, finisare și verificare, ca să ajungă la tine cu un sound plăcut, echilibrat și potrivit poveștii tale.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[{ icon: Mic2, label: "Voce clară" }, { icon: Headphones, label: "Finisare audio" }, { icon: Music2, label: "Piesă unică" }].map((x) => (
                <div key={x.label} className="border border-white/10 p-5 hover:border-crimson transition-colors">
                  <x.icon className="w-5 h-5 text-crimson" />
                  <div className="mt-3 font-mono text-xs uppercase tracking-widest">{x.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative aspect-square bg-ink-800 border border-white/10 hud-frame overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full px-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">PLAYING / sample 01.mp3</div>
                  <div className="font-mono text-[10px] text-crimson">● LIVE</div>
                </div>
                <div className="flex items-end justify-between h-40 gap-1">
                  {Array.from({ length: 32 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-crimson"
                      style={{ height: `${20 + Math.abs(Math.sin(i * 0.5)) * 100}%`, opacity: 0.3 + Math.abs(Math.cos(i * 0.4)) * 0.7 }}
                    />
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button aria-label="Redă mostra audio" className="w-12 h-12 bg-crimson flex items-center justify-center hover:bg-crimson-light transition-colors">
                    <Play className="w-5 h-5 text-white fill-white" />
                  </button>
                  <div className="flex-1 h-1 bg-white/10">
                    <div className="h-full w-1/3 bg-crimson" />
                  </div>
                  <span className="font-mono text-xs text-white/50">01:23 / 03:45</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 flex items-end justify-between">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// Voices</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">Ce zic <span className="text-crimson">ai noștri</span></h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(reviews.length ? reviews : []).slice(0, 6).map((r) => (
              <div key={r.id} className="bg-ink-800 border border-white/10 p-8 hover:border-crimson transition-colors">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 text-crimson fill-crimson" />)}
                </div>
                <h4 className="font-display font-bold text-lg">{r.title}</h4>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{r.body}</p>
                <div className="mt-6 font-mono text-xs uppercase tracking-widest text-white/40">— {r.customer_name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5" data-testid="faq-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">// FAQ</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">Întrebări frecvente</h2>
            <p className="mt-4 text-white/60 max-w-2xl">Răspunsuri rapide despre livrare, revizii, drepturi și ce primești la final.</p>
          </div>
          <div className="divide-y divide-white/10 border border-white/10 bg-ink-800 hud-frame">
            {faqItems.slice(0, 5).map((item) => (
              <details key={item.id || item.question} className="group p-5 open:bg-black/20">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-display text-lg font-bold">{item.question}</span>
                  <span className="font-mono text-crimson group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-white/65 max-w-3xl">{item.answer}</p>
              </details>
            ))}
          </div>
          <Link to="/faq" className="inline-flex mt-6 font-mono text-xs uppercase tracking-widest text-crimson hover:text-crimson-light">
            Vezi toate întrebările <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter">
            Gata să faci<br /><span className="text-crimson">cadoul perfect?</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">Comandă acum și primește piesa ta unică. Express în 24h sau standard în 3-5 zile.</p>
          <Link to="/comanda" className="inline-block mt-10">
            <Button size="lg" data-testid="cta-order-bottom" className="bg-crimson hover:bg-crimson-light text-white rounded-none font-mono uppercase tracking-widest text-xs px-12 py-7 crimson-glow">
              Începe acum <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;

